import styles from "../styles/ProjectsPage.module.css";
import cardStyles from "../styles/ProjectCard.module.css";
import { useMemo } from "react";

export default function ProjectsPage() {
	const categories = useMemo(() => [
		{ name: "Live/Ongoing", projects: projects.filter(p => p.category === "Live/Ongoing") },
		{ name: "In Progress", projects: projects.filter(p => p.category === "In Progress") },
		{ name: "In The Queue", projects: projects.filter(p => p.category === "In The Queue") },
		{ name: "People I Help", projects: projects.filter(p => p.category === "People I Help") },
		{ name: "Parked Projects", projects: projects.filter(p => p.category === "Parked Projects") },
		{ name: "No longer with us", projects: projects.filter(p => p.category === "No longer with us") }
	], []);

	return (
		<>
			<h3>My Recent or Current Projects</h3>
			{categories.map((category) => (
				<div key={category.name}>
					<h4 className={styles.categoryTitle}>{category.name}</h4>
					<div className={styles.container}>
						{category.projects.map((project) => {
							const content = (
								<>
									<div className={cardStyles.title}>{project.title}</div>
									<img src={project.imageSrc} alt={project.title} />
									<p>{project.description}</p>
									<div className={cardStyles.tags}>
										{project.tags.map((tag) => (
											<span key={tag} className={tag}>
												{/* we need to translate some of the tags, so CSharp would become C#, VBNet would become VB.Net */}
												{tag === "CSharp"
													? "C#"
													: tag === "VBNET"
													? "VB.Net"
													: tag === "MurderMystery"
													? "Murder Mystery"
													: tag === "DotNet"
													? ".Net"
													: tag}
											</span>
										))}
									</div>
								</>
							);

							return project.link ? (
								<a
									key={project.id}
									className={cardStyles.card}
									href={project.link}
									target="_blank"
									rel="noopener noreferrer"
									aria-label={`Open ${project.title}`}
								>
									{content}
								</a>
							) : (
								<article
									key={project.id}
									className={cardStyles.card}
									aria-label={project.title}
								>
									{content}
								</article>
							);
						})}
					</div>
				</div>
			))}
			<p>Computer photos: <a href="https://commons.wikimedia.org/wiki/File:ZXSpectrum48k.jpg">ZX Spectrum</a> and <a href="https://commons.wikimedia.org/wiki/File:Atari_1040STf.jpg">Atari ST</a> by Bill Bertram, <a href="https://creativecommons.org/licenses/by-sa/2.5/">CC BY-SA 2.5</a>. Resized for display.</p>
		</>
	);
}

type Project = {
	id: number;
	title: string;
	description: string;
	imageSrc: string;
	tags: string[];
	link?: string;
	category: string;
};

const projects: Project[] = [
	// Live/Ongoing
	{
		id: 1,
		title: "Real Life",
		description:
			"Most of my time as a software developer is spent as an employee working on software for the printing industry.  I am also a scrum master for the development team.",
		imageSrc: "RealLife.png",
		tags: ["TypeScript", "CSharp", "VBNet", "Scrum"],
		link: "",
		category: "Live/Ongoing",
	},
	{
		id: 2,
		title: "Rockett Viewer",
		description:
			"A VS Code extension that allows you to view your code within a VS Code tab inside a mobile phone frame.  Great for testing how you application will look, or grabbing a screenshot for a presentation.  Available on the Visual Studio Marketplace.",
		imageSrc: "RockettViewer.png",
		tags: ["VSCode", "JavaScript"],
		link: "https://marketplace.visualstudio.com/items?itemName=WayneSRockett.rockett-viewer",
		category: "Live/Ongoing",
	},
	{
		id: 4,
		title: "Who Dunnit Club",
		description:
			"I write and host murder mystery nights with around fifty people attending each event.  I have run Harry Potter murders, Titanic, Oscars and various other themed murders.",
		imageSrc: "WhoDunnitClub.png",
		tags: ["MurderMystery"],
		link: "https://whodunnit.club/",
		category: "Live/Ongoing",
	},
	{
		id: 5,
		title: "Valheim Cooking",
		description:
			"A simple website for the different food items in the game Valheim.",
		imageSrc: "ValheimCooking.png",
		tags: ["React", "TypeScript", "JavaScript", "CSS"],
		link: "https://valheim.cooking/",
		category: "Live/Ongoing",
	},
	{
		id: 6,
		title: "YorkCode.dev",
		description:
			"A programming language with Yorkshire dialect keywords - as straightforward as a chat down t'pub. Learn programming while having a proper laugh with honest code that does what it says on t'tin.",
		imageSrc: "YorkCode.png",
		tags: ["JavaScript", "TypeScript"],
		link: "https://yorkcode.dev/",
		category: "Live/Ongoing",
	},
	{
		id: 7,
		title: "Rockett Paint",
		description:
			"A lightweight online homage to MS Paint handy for adding those little things that you need to add to images sometimes. It also includes an image converter.",
		imageSrc: "RockettPaint.png",
		tags: ["React", "TypeScript"],
		link: "",
		category: "No longer with us",
	},
	{
		id: 8,
		title: "Rockett Timer",
		description:
			"An application for timing various things such as stopwatch, countdown, agenda items, scrum meetings.",
		imageSrc: "RockettTimer.png",
		tags: ["React", "TypeScript"],
		link: "https://www.timer.waynerockett.com/",
		category: "Live/Ongoing",
	},
	{
		id: 17,
		title: "I Am The Meter Man",
		description:
			"Advice for reading gas meters.",
		imageSrc: "/projects/meter-man.jpg",
		tags: ["Website"],
		link: "/meterman",
		category: "Live/Ongoing",
	},
	{
		id: 29,
		title: "ZX Loading Screen",
		description:
			"A ZX Spectrum loading screen generator written as part of a Gemini AI course run by dev.to and Google.",
		imageSrc: "ZXLoadingScreen.png",
		tags: ["ZXSpectrum", "AI", "React"],
		link: "http://zxspectrum.waynerockett.com/",
		category: "Live/Ongoing",
	},
	{
		id: 30,
		title: "Retro Tier List",
		description:
			"Retro Tier List is a simple tier list maker with a retro feel. Uploaded images are converted to ZX Spectrum graphics.",
		imageSrc: "RetroTierList.png",
		tags: ["React", "ZXSpectrum", "Retro"],
		link: "https://retrotierlist.xyz/",
		category: "Live/Ongoing",
	},
	// In Progress
	{
		id: 45,
		title: "The Northern Sea",
		description:
			"A modern remake of the ZX Spectrum game Viking Raiders, staying as close as possible to the original.",
		imageSrc: "VikingRaiders.png",
		tags: ["JavaScript"],
		link: "https://northern-sea.bugbusters.games/",
		category: "Live/Ongoing",
	},
	{
		id: 3,
		title: "Rockett Writer",
		description:
			"A desktop text editor that, well, quite frankly focuses on the features that I personally wanted.",
		imageSrc: "RockettWriter.png",
		tags: ["React", "TypeScript"],
		link: "",
		category: "In The Queue",
	},
	{
		id: 9,
		title: "Cas Proud",
		description:
			"Application for local news and events.",
		imageSrc: "/projects/cas-proud.jpg",
		tags: ["React", "TypeScript"],
		link: "https://casproud.co.uk/",
		category: "Live/Ongoing",
	},
	{
		id: 11,
		title: "Bowel Surgery Recovery Tracker",
		description:
			"After discovering I had cancer I had bowel surgery, and decided this was my way of passing forward the kindness and care I was shown.",
		imageSrc: "https://image.thum.io/get/width/1200/crop/800/noanimate/https://recovery-tracker-liard.vercel.app/",
		tags: ["React", "TypeScript", "Healthcare"],
		link: "",
		category: "In The Queue",
	},
	{
		id: 13,
		title: "Idle Sprint Planning Simulator",
		description:
			"Yes, this really is what it sounds like.  A simple sprint board simulator to help teach about sprints.",
		imageSrc: "Sprint.png",
		tags: ["React", "Game", "Scrum"],
		link: "http://idlesprintplanningsimulator.xyz",
		category: "Live/Ongoing",
	},
	{
		id: 15,
		title: "Cas Vegas Sunday League",
		description:
			"A retro Sunday league football manager game.",
		imageSrc: "CasVegasSundayLeague.png",
		tags: ["Game", "Retro"],
		link: "",
		category: "In The Queue",
	},
	{
		id: 31,
		title: "Deck Warrior",
		description:
			"An RPG / Dungeon Crawler game using a single deck of playing cards, based on the game Scoundrel.",
		imageSrc: "DeckWarrior.png",
		tags: ["Game", "RPG"],
		link: "https://deck-warrior.bugbusters.games/",
		category: "In The Queue",
	},
	{
		id: 32,
		title: "Dungeon Coder Carl",
		description:
			"A dungeon crawling coding adventure game.",
		imageSrc: "Carl.png",
		tags: ["React", "TypeScript", "Game"],
		link: "https://dungeon-coder-carl.bugbusters.games/",
		category: "In The Queue",
	},
	{
		id: 33,
		title: "Windvale",
		description: "An application for running D&D-style games online.",
		imageSrc: "https://image.thum.io/get/width/1200/crop/800/noanimate/https://windvale.onrender.com/",
		tags: ["React", "TypeScript", "DotNet", "Signalr"],
		link: "https://windvale.onrender.com/",
		category: "In Progress",
	},
	{
		id: 35,
		title: "Zigball Manager",
		description: "An Anarchy Online-inspired Zigball management game.",
		imageSrc: "https://image.thum.io/get/width/1200/crop/800/noanimate/https://github.com/WayneRockett/zigball-manager",
		tags: ["Game"],
		link: "https://zigball.bugbusters.games/",
		category: "Live/Ongoing",
	},
	// In The Queue
	{
		id: 20,
		title: "Telefax Cup",
		description:
			"A teletext-inspired football cup management game",
		imageSrc: "TelefaxCup.jpg",
		tags: ["Game", "Retro", "ZXSpectrum"],
		link: "https://telefax.bugbusters.games/",
		category: "In Progress",
	},
	{
		id: 21,
		title: "The Wild Bunch",
		description:
			"A homage to the wonderful ZX Spectrum game The Wild Bunch, well, one of the best games of all time I think.",
		imageSrc: "TheWildBunch.png",
		tags: ["Game", "Retro", "ZXSpectrum"],
		link: "",
		category: "In The Queue",
	},
	{
		id: 22,
		title: "Space Raiders (Viking Raiders)",
		description:
			"A remake of the ZX Spectrum strategy game Viking Raiders.",
		imageSrc: "VikingRaiders.png",
		tags: ["Game", "Retro", "ZXSpectrum"],
		link: "https://github.com/WayneRockett/space-raiders",
		category: "In Progress",
	},
	{
		id: 24,
		title: "Robo Sports Agent",
		description:
			"As an agent in the world of robot sports, can you build the robots that will dominate the world, or sports.",
		imageSrc: "RoboSportsAgent.png",
		tags: ["Game"],
		link: "",
		category: "In The Queue",
	},
	{
		id: 34,
		title: "Zix Launcher",
		description:
			"A companion application for the MMO Anarchy Online. Screenshot by Funcom Productions (CC BY-SA 3.0): https://commons.wikimedia.org/wiki/File:Anarchy_Online_Land_Control_Area.png",
		imageSrc:
			"https://upload.wikimedia.org/wikipedia/commons/7/75/Anarchy_Online_Land_Control_Area.png",
		tags: ["React", "TypeScript", "Electron"],
		link: "",
		category: "In The Queue",
	},
	// People I Help
	{
		id: 16,
		title: "Joy Bangla",
		description:
			"A local Bangladeshi restaurant who do some very excellent onion bhajis.",
		imageSrc: "JoyBangla.png",
		tags: ["Website", "Restaurant"],
		link: "http://joybangla.menu/",
		category: "No longer with us",
	},
	{
		id: 18,
		title: "Connies Friends",
		description:
			"A local dog walking service with Connie the dog.",
		imageSrc: "ConniesFriends.png",
		tags: ["Website"],
		link: "https://www.conniesfriends.co.uk/",
		category: "People I Help",
	},
	{
		id: 19,
		title: "Tranquillity Spa",
		description:
			"In need of a relaxation and recovery, then Tranquillity Spa is the place for you.",
		imageSrc: "TranquillitySpa.png",
		tags: ["Website", "Spa"],
		link: "https://tranquillityspa.biz/",
		category: "People I Help",
	},
	// Parked Projects
	{
		id: 10,
		title: "The Longest Marriage",
		description:
			"The Longest Marriage is a short story I wrote for a writing competition, which did very well and I intend, one day, to make into a full book.",
		imageSrc: "TheLongestMarriage.png",
		tags: ["Writing"],
		link: "",
		category: "Parked Projects",
	},
	{
		id: 12,
		title: "Bug Buster Deck Builder",
		description:
			"A card deck building game.",
		imageSrc: "BugBusterDeckBuilder.png",
		tags: ["Game"],
		link: "",
		category: "Parked Projects",
	},
	{
		id: 23,
		title: "BB Crawler",
		description:
			"A retro dungeon crawler game.",
		imageSrc: "BBCrawler.png",
		tags: ["Game", "Retro"],
		link: "",
		category: "Parked Projects",
	},
	{
		id: 25,
		title: "Stock Car Racer",
		description:
			"A remake of a game I made many many years ago.",
		imageSrc: "StockCarRacer.png",
		tags: ["Game", "Retro"],
		link: "",
		category: "Parked Projects",
	},
	{
		id: 26,
		title: "The Wild West World Bunch",
		description:
			"A mash up of the game The Wild Bunch, but set in the West World theme park.",
		imageSrc: "TheWildWestWorldBunch.png",
		tags: ["Game"],
		link: "",
		category: "No longer with us",
	},
	{
		id: 27,
		title: "Pocket One Dice Adventure",
		description:
			"Mini D&D adventures that you can play online or print out and take with you to play on the go.  All you need is a pencil and one die.",
		imageSrc: "PocketOneDiceAdventure.png",
		tags: ["Game", "DnD"],
		link: "",
		category: "Parked Projects",
	},
	{
		id: 28,
		title: "Rockett Review",
		description:
			"A book about time management, project management and improving communication in the work environment.",
		imageSrc: "RockettReview.png",
		tags: ["Writing", "Book"],
		link: "",
		category: "Parked Projects",
	},
	// No longer with us
	{
		id: 36,
		title: "The Anker",
		description: "A ZX Spectrum text adventure game that, for about two days, held the record for the most unique locations.",
		imageSrc: "/projects/zx-spectrum.jpg",
		tags: ["Game", "Retro", "ZXSpectrum"],
		category: "No longer with us",
	},
	{
		id: 37,
		title: "The Village",
		description: "A 16-bit resource management game.",
		imageSrc: "/projects/atari-st.jpg",
		tags: ["Game", "Retro", "AtariST"],
		category: "No longer with us",
	},
	{
		id: 38,
		title: "The Village 2",
		description: "A follow-up to The Village, with computer-controlled opponents to battle.",
		imageSrc: "/projects/atari-st.jpg",
		tags: ["Game", "Retro", "AtariST"],
		category: "No longer with us",
	},
	{
		id: 39,
		title: "Collect4",
		description: "An Excel spreadsheet for automatically routing deliveries and collections to vehicles.",
		imageSrc: "/projects/excel.svg",
		tags: ["Excel"],
		category: "No longer with us",
	},
	{
		id: 40,
		title: "WMan",
		description: "A handheld scanner application for warehouse inventory management.",
		imageSrc: "/projects/scanner.svg",
		tags: ["Warehouse"],
		category: "No longer with us",
	},
	{
		id: 41,
		title: "ProgAid",
		description: "A COBOL application to aid code version management, automated testing and documentation.",
		imageSrc: "/projects/cobol.svg",
		tags: ["COBOL"],
		category: "No longer with us",
	},
	{
		id: 42,
		title: "Mega-Lo-Mailia",
		description: "A Unix-based email client usable through a UI or the command line.",
		imageSrc: "/projects/email.svg",
		tags: ["Unix", "Email"],
		category: "No longer with us",
	},
	{
		id: 43,
		title: "The Race",
		description: "A ZX Spectrum racing and gambling game.",
		imageSrc: "/projects/zx-spectrum.jpg",
		tags: ["Game", "Retro", "ZXSpectrum"],
		category: "No longer with us",
	},
	{
		id: 44,
		title: "The Tardis",
		description: "An Excel spreadsheet for managing a holiday company.",
		imageSrc: "/projects/holiday.svg",
		tags: ["Excel", "Travel"],
		category: "No longer with us",
	},
];
